import { Box, BoxProps, Column, Row } from "@hightouch/htui";
import { FC, PropsWithChildren, ReactNode } from "react";

import { Helmet } from "react-helmet";
import { Paper, PAPER_RIGHT_PADDING_PX } from "src/components/paper";
import { useHeaderHeight } from "src/contexts/header-height-context";
import { Breadcrumbs } from "./breadcrumbs";
import { withCurrentPageCrumb } from "./crumbs";
import { PageBanners } from "./header/page-banners";
import { PageCardTopBar } from "./page-card-top-bar";
import { Crumb, PageBg } from "./types";

export type NewPageProps = PropsWithChildren<{
  /**
   * If true, the page banners (including `outsideTopbar`) are not displayed.
   * @default false
   */
  hideBanners?: boolean;
  /** Browser-tab (Helmet) title — never rendered on the page. */
  title?: string;
  /** Optional sidebar to display in the page */
  sidebar?: ReactNode;
  /** A panel beside the sidebar that slides in over the page card while open. Requires `sidebar`. */
  sidebarPanel?: SidebarPanel;
  /** If true, the page will be constrained to the height of the screen (no body scroll) */
  contain?: boolean;
  /** Optional additional content to display above the page content and below the page banners */
  outsideTopbar?: ReactNode;
  /** If true, renders children directly without the Paper wrapper (full-bleed layout) */
  flush?: boolean;
  /** Breadcrumbs rendered in a `PageCardTopBar` at the top of the page card.
   *  The current page (`title`) is appended when the trail ends in a link.
   *  Not rendered in `flush` mode (there is no card). */
  crumbs?: Crumb[];
}>;

export type SidebarPanel = {
  isOpen: boolean;
  /** The open sidebar's width, so the panel takes the rest of the row. */
  sidebarWidthPx: number;
  /** Where the host renders or portals its panel content. */
  ref: (node: HTMLDivElement | null) => void;
  label: string;
};

export const NewPage: FC<NewPageProps> = ({
  children,
  title,
  sidebar,
  sidebarPanel,
  outsideTopbar,
  crumbs,
  contain = false,
  hideBanners = false,
  flush = false,
}) => {
  const metadataContent = (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  );

  const bannerContent = !hideBanners && (
    <PageBanners>{outsideTopbar}</PageBanners>
  );

  const crumbsBar = crumbs?.length ? (
    <PageCardTopBar>
      <Breadcrumbs showIcon crumbs={withCurrentPageCrumb(crumbs, title)} />
    </PageCardTopBar>
  ) : null;

  const pageContent = flush ? (
    <Column
      flex={1}
      minHeight={0}
      overflow={contain ? "hidden" : undefined}
      overscrollBehavior={contain ? "contain" : undefined}
    >
      {children}
    </Column>
  ) : (
    <Paper
      overflow={contain ? "hidden" : undefined}
      overscrollBehavior={contain ? "contain" : undefined}
      // If we have a sidebar, the content will be inside a row.
      // Setting the flex to 1 will make the content take up the remaining space.
      flex={sidebar ? 1 : undefined}
      minHeight={contain || sidebar ? 0 : undefined}
    >
      {crumbsBar}
      {children}
    </Paper>
  );

  if (sidebar && sidebarPanel) {
    return (
      <Box display="grid" gridTemplateRows="auto 1fr">
        {metadataContent}
        {bannerContent}
        <SidebarPanelRow panel={sidebarPanel} sidebar={sidebar}>
          {pageContent}
        </SidebarPanelRow>
      </Box>
    );
  }

  if (sidebar) {
    return (
      <Box display="grid" gridTemplateRows="auto 1fr">
        {metadataContent}
        {bannerContent}
        <Row flex={1} minWidth={0}>
          {pageContent}
          {sidebar}
        </Row>
      </Box>
    );
  }

  return (
    <Box
      display="grid"
      gridTemplateRows="auto 1fr"
      h="100%"
      maxH={contain ? "100vh" : "100%"}
      overflow={contain ? "hidden" : undefined}
    >
      {metadataContent}
      {bannerContent}
      {pageContent}
    </Box>
  );
};

/**
 * Page card, sidebar, and panel side by side in a row twice the viewport
 * wide. Opening slides the row left so the sidebar and panel fill the view.
 * Column widths stay fixed so the page card never reflows per frame.
 */
const SidebarPanelRow: FC<
  PropsWithChildren<{ panel: SidebarPanel; sidebar: ReactNode }>
> = ({ panel, sidebar, children }) => {
  const { headerHeight } = useHeaderHeight();
  const sidebarWidth = `${panel.sidebarWidthPx}px`;

  return (
    // clip rather than hidden: a scroll container would stop the sidebar sticking.
    <Box minWidth={0} overflowX="clip">
      <Box
        display="grid"
        width={`calc(200% - ${sidebarWidth})`}
        gridTemplateColumns={`minmax(0, 1fr) auto calc((100% - ${sidebarWidth}) / 2)`}
        // No transform while closed: it would make the overlaid sidebar's fixed positioning relative to this row.
        transform={
          panel.isOpen
            ? `translateX(calc((${sidebarWidth} - 100%) / 2))`
            : undefined
        }
        transitionProperty="transform"
        transitionDuration="normal"
        transitionTimingFunction="easeOutCubic"
        _mediaReduceMotion={{ transitionDuration: "0s" }}
      >
        <Column
          minWidth={0}
          visibility={panel.isOpen ? "hidden" : "visible"}
          transitionProperty="visibility"
          transitionDuration="normal"
          _mediaReduceMotion={{ transitionDuration: "0s" }}
          aria-hidden={panel.isOpen || undefined}
        >
          {children}
        </Column>
        {sidebar}
        <Column
          as="section"
          aria-label={panel.label}
          position="sticky"
          top={0}
          alignSelf="start"
          height={`calc(100vh - ${headerHeight}px)`}
          minWidth={0}
          pt={4}
          pr={`${PAPER_RIGHT_PADDING_PX}px`}
          visibility={panel.isOpen ? "visible" : "hidden"}
          transitionProperty="visibility"
          transitionDuration="normal"
          _mediaReduceMotion={{ transitionDuration: "0s" }}
          aria-hidden={!panel.isOpen || undefined}
        >
          <Column
            ref={panel.ref}
            flex={1}
            minHeight={0}
            bg="base.white"
            borderWidth="hairline"
            borderColor="base.border"
            borderTopRadius="xl"
            overflow="hidden"
          />
        </Column>
      </Box>
    </Box>
  );
};

type PageCardHeaderProps = {
  flush?: boolean;
  removeBoxShadow?: boolean;
  hasBorder?: boolean;
} & BoxProps;

export const PageCardHeader: FC<PropsWithChildren<PageCardHeaderProps>> = ({
  children,
  flush,
  removeBoxShadow = false,
  hasBorder = false,
  ...props
}) => {
  const borderProps = hasBorder
    ? ({
        borderBottomWidth: "hairline",
        borderBottomStyle: "solid",
        borderColor: "base.divider",
      } as const)
    : {};

  return (
    <Row
      px={flush ? 0 : 6}
      py={flush ? 0 : 4}
      gap={4}
      borderTopRadius="2xl"
      zIndex={1}
      boxShadow={
        removeBoxShadow
          ? undefined
          : "0 3px 6px -2px rgba(0, 0, 0, 0.02), 0 1px 1px 0px rgba(0, 0, 0, 0.02), 0 4px 20px 0px #FFF"
      }
      overflow="hidden"
      {...borderProps}
      {...props}
    >
      {children}
    </Row>
  );
};

type PageCardContentProps = {
  hasGradient?: boolean;
  flush?: boolean;
  variant?: PageBg;
} & Omit<BoxProps, "bg">;

const bgGradient = "linear(180deg, white, gray.50)";

export const PageCardContent: FC<PropsWithChildren<PageCardContentProps>> = ({
  hasGradient = false,
  children,
  flush,
  variant = "light",
  ...props
}) => {
  return (
    <Column
      flex={1}
      minHeight={0}
      p={flush ? 0 : 6}
      bg={variant === "dark" ? "base.lightBackground" : "white"}
      bgGradient={hasGradient ? bgGradient : undefined}
      {...props}
    >
      {children}
    </Column>
  );
};
